import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { GO_SHORTCUTS, currentPageShortcuts, isTyping, layerOpen } from "./shortcuts";

const ShortcutsOverlay = lazy(() => import("./ShortcutsOverlay").then((m) => ({ default: m.ShortcutsOverlay })));

/** How long after "g" the second key still counts. */
const SEQUENCE_MS = 1200;

/**
 * Mounted once by the shell. Listens for the global shortcuts and opens the
 * "?" overlay (its code loads the first time it opens). `canOpen` says which
 * pages this person may open, so a shortcut never leads to a page they
 * cannot see.
 */
export function ShortcutsHost({ go, canOpen }: { go: (page: string) => void; canOpen: (page: string) => boolean }) {
  const [open, setOpen] = useState(false);
  const pendingG = useRef<number>(0);
  const nav = useRef({ go, canOpen });
  nav.current = { go, canOpen };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.metaKey || e.ctrlKey) return;
      if (isTyping(e.target)) return;
      if (e.key === "?") {
        e.preventDefault();
        setOpen((x) => !x);
        return;
      }
      if (layerOpen()) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (key === "/") {
        const trigger = document.querySelector<HTMLButtonElement>(".gsearch-trigger");
        if (trigger) {
          e.preventDefault();
          trigger.click();
        }
        return;
      }
      if (pendingG.current && Date.now() - pendingG.current < SEQUENCE_MS) {
        pendingG.current = 0;
        const target = GO_SHORTCUTS.find((g) => g.key === key);
        if (target && nav.current.canOpen(target.page)) {
          e.preventDefault();
          nav.current.go(target.page);
        }
        return;
      }
      if (key === "g" && !e.shiftKey) {
        pendingG.current = Date.now();
        return;
      }
      const page = currentPageShortcuts().find((s) => s.key === key);
      if (page) {
        e.preventDefault();
        page.run();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <ShortcutsOverlay onClose={() => setOpen(false)} canOpen={canOpen} />
    </Suspense>
  );
}
